// src/core/redux/services/adminAuditApi.ts
//
// Platform audit-log viewer (superadmin). /admin/audits lists all activity and
// takes ?tenantId= to scope; /admin/audits/:tenantId is the same thing with the
// tenant in the path.
//
// Filtering and paging are SERVER-side. They used to be client-side, which
// worked only because the endpoint returned the entire ActivityLog table on
// every call — an unbounded query that would not have survived real volume.
// Now that the server pages, sending the filters with the request is what keeps
// the viewer looking at all 1,700+ rows rather than only the first page of them.

import { baseApi } from "./baseApi";

export interface AuditLogRow {
  id: string;
  tenantId?: string | null;
  userId?: string | null;
  user?: { id: string; name?: string; email?: string } | null;
  module?: string | null;
  action: string;
  entityId?: string | null;
  /** Correlation id of the request that produced this row; null for system work. */
  requestId?: string | null;
  details?: unknown;
  ipAddress?: string | null;
  userAgent?: string | null;
  timestamp: string;
}

export interface AuditQuery {
  tenantId?: string | null;
  module?: string;
  action?: string;
  userId?: string;
  entityId?: string;
  /** The support lookup: paste the id from the error the tenant reported. */
  requestId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface AuditPage {
  data: AuditLogRow[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

/** Tolerate both the paged envelope and any older bare-array response. */
const toPage = (r: any): AuditPage => {
  if (Array.isArray(r)) {
    return { data: r, meta: { total: r.length, page: 1, limit: r.length, totalPages: 1 } };
  }
  const data = Array.isArray(r?.data) ? r.data : [];
  const meta = r?.meta ?? { total: data.length, page: 1, limit: data.length, totalPages: 1 };
  return { data, meta };
};

const toQueryString = (q: AuditQuery) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const adminAuditApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAuditLogs: builder.query<AuditPage, AuditQuery | void>({
      query: (q) => `admin/audits${toQueryString((q as AuditQuery) ?? {})}`,
      transformResponse: toPage,
      providesTags: [{ type: "AuditLog", id: "ALL" }],
    }),
    getTenantAuditLogs: builder.query<AuditPage, { tenantId: string } & AuditQuery>({
      query: ({ tenantId, ...q }) => `admin/audits/${tenantId}${toQueryString(q)}`,
      transformResponse: toPage,
      providesTags: (_r, _e, { tenantId }) => [{ type: "AuditLog", id: tenantId }],
    }),
  }),
});

export const { useGetAuditLogsQuery, useGetTenantAuditLogsQuery } = adminAuditApi;
